import { TrustAccountStatus, VendorStatus, AccountingSyncStatus } from '../../types';

export interface FirmBankingSystemAccountData {
  accountNumber: string;
  routingNumber: string;
  balance: string;
  status: string;
  withdrawalAvailable: boolean;
}

export type IntegrationProvisioningData = {
  ofac?: {
    status: string;
    reason: string | null;
  };
  provisioning: {
    error?: {
      status: string;
      reason: string | null;
    };
    status: keyof typeof OFAC_STATUSES | keyof typeof INTEGRATION_STATUS;
  };
};

export interface FirmBankingData {
  accounts: {
    case: FirmBankingSystemAccountData | null;
    rev: FirmBankingSystemAccountData | null;
    exp: FirmBankingSystemAccountData | null;
  };
  canStartIntegration: boolean;
}

export enum INTEGRATION_STATUS {
  InProgress = 'In Progress',
  Error = 'Error',
  CriticalError = 'Critical Error',
  Completed = 'Completed',
}

export enum OFAC_STATUSES {
  PENDING_VERIFICATION = 'Pending Verification',
  VERIFIED = 'Verified',
  UNDER_REVIEW = 'Under Review',
  REJECTED = 'Rejected',
  IGNORED = 'Ignored',
}

export enum ProvisioningStatus {
  NotStarted = 'NotStarted',
  InProgress = 'InProgress',
  Error = 'Error',
  CriticalError = 'CriticalError',
  Completed = 'Completed',
}

export enum SYSTEM_ACCOUNT_LABELS {
  rev = 'Revenue Recognition',
  exp = 'Expense Reimbursement',
  case = 'Case Clearing',
}

export enum SYSTEM_ACCOUNT_STATUSES {
  INACTIVE = 'INACTIVE',
  ACTIVE = 'ACTIVE',
  BLOCKED = 'BLOCKED',
  CLOSURE_INITIATED = 'CLOSURE_INITIATED',
  CLOSED = 'CLOSED',
}

export interface UnrestrictedAccountSubmitFormProps {
  name: string;
  payeeType: string;
  vendorType?: string;
  phoneNumber?: string;
  email?: string;
  confirmVendorProvisioning?: boolean;
  achEnabled?: boolean;
  checkMailingEnabled?: boolean;
  type?: string;
  accountNumber?: string;
  routingNumber?: string;
  addressLine1?: string;
  addressLine2?: string;
  city?: string;
  state?: string;
  zipCode?: string;
}

export interface VendorAccountSubmitFormProps {
  name: string;
  vendorType?: string;
  mailingPhoneNumber?: string;
  email?: string;
  isVendorConfirmationRequired?: boolean;
  achEnabled?: boolean;
  checkMailingEnabled?: boolean;
  defaultAchEnabled?: boolean;
  defaultCheckMailingEnabled?: boolean;
  sameAsVendorAddress?: boolean;
  accountType?: string;
  accountNumber?: string;
  routingNumber?: string;
  mailingAddress1?: string;
  mailingAddress2?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZip?: string;
  participationAddress1?: string;
  participationAddress2?: string;
  participationCity?: string;
  participationState?: string;
  participationZip?: string;
  defaultPaymentMethod?: 'ACH' | 'CHECK' | 'V';
  tin?: string | null;
  isAddressVerified?: boolean;
  isMailingAddressVerified?: boolean;
  contactName?: string;
  notes?: string;
  externalVendorId?: string | null;
  achAccounts?: {
    id?: number | string;
    nickname: string;
    accountType: string;
    routingNumber: string;
    accountNumber?: string;
    maskedAccountNumber?: string;
    isDefault: boolean;
  }[];
  checkAccounts?: {
    id?: number | string;
    nickname: string;
    payeeName?: string;
    recipientName?: string;
    address: string;
    addressLine2?: string;
    city: string;
    state: string;
    zip: string;
    country?: string;
    isDefault: boolean;
  }[];
  files?: File[];
}

export interface VendorAccountTableItem {
  createdAt?: string;
  id: number;
  name: string;
  vendorType?: string;
  defaultPaymentMethod?: string;
  status: VendorStatus;
  isVendorConfirmationRequired?: boolean;
  updatedAt: string;
}

export interface VendorPaymentHistoryTableItem {
  caseId: number;
  updatedAt: string;
  caseReferenceId: string;
  plaintiffName: string;
  type: string;
  method: string;
  amount: number;
  status: string;
  recoveredAmount?: number;
  billLink: string | null;
  billError: string | null;
  billSyncStatus?: AccountingSyncStatus | null;
  billPaymentLink: string | null;
  billPaymentError: string | null;
  billPaymentSyncStatus?: AccountingSyncStatus | null;
  vendorLink: string | null;
  checkDetails?: {
    payee: string | null;
    nickname: string | null;
    payeeName: string | null;
    recipientName: string | null;
    address1: string | null;
    address2: string | null;
    city: string | null;
    countryState: string | null;
    zip: string | null;
    phoneNumber: string | null;
  } | null;
  achDetails?: {
    payee: string | null;
    nickname: string | null;
    accountHolderName: string | null;
    accountType: string | null;
    routingNumber: string | null;
    accountNumber: string | null;
  } | null;
}

export interface VendorDocumentTableItem {
  id: number;
  fileName: string;
  fileSize: string;
  uploadedBy: string;
  uploadedByIsDeactivated?: boolean;
  uploadDate: string;
  uploadDateFull: string;
}

export interface TrustAccountSubmitFormProps {
  name: string;
  type: string;
  accountNumber: string;
  routingNumber: string;
}

export interface TrustAccountTableItem {
  createdAt: string;
  id: number;
  name: string;
  type: string;
  accountNumber: string;
  routingNumber: string;
  isEwsVerified: string;
  status: TrustAccountStatus | string;
  canBeDeleted: boolean;
}

export interface FirmBankingWithdrawFormSubmitProps {
  fromSystemAccountType: string;
  toUnrestrictedAccountId: string;
  amount: string;
}

export enum AccountTypeShortCuts {
  'Revenue Recognition' = 'REV',
  'Expense Reimbursement' = 'EXP',
}

export enum MenuOption {
  Edit = 'Edit',
  Delete = 'Delete',
}

export interface BankingInfo {
  vendorParticipationId: number;
  name: string;
  mailing: {
    phoneNumber: string;
    address1: string;
    address2?: string;
    city: string;
    state: string;
    zip: string;
    isAddressVerified: boolean;
    provisioningStatus: ProvisioningStatus;
  };
  bank: {
    hasAccountNumber: boolean;
    routingNumber: string;
    accountType: 'Checking' | 'Savings';
    provisioningStatus: ProvisioningStatus;
  };
  participationAddress1: string;
  participationAddress2?: string;
  participationCity: string;
  participationState: string;
  participationZip: string;
  isparticipationAddressVerified: boolean;
  consentRequired: boolean;
  consentDocPath?: string;
  vendorType: 'LIEN_HOLDER';
  email: string;
  defaultPaymentMethod: 'ACH' | 'CHECK' | 'V';
  contactName: string;
}

export interface BankingFormData {
  name: string;
  accountType?: string;
  accountNumber?: string;
  routingNumber?: string;
  mailingAddress1?: string;
  mailingAddress2?: string;
  mailingCity?: string;
  mailingState?: string;
  mailingZipCode?: string;
  phoneNumber?: string;
  isAccountDetailsActive?: boolean;
  isAddressDetailsActive?: boolean;
  isAddressVerified?: boolean;
  participationAddress1?: string;
  participationAddress2?: string;
  participationCity?: string;
  participationState?: string;
  participationZip?: string;
  vendorType?: string;
  email?: string;
  isparticipationAddressVerified?: boolean;
  defaultPaymentMethod?: string;
  isParticipationAddressActive?: boolean;
  contactName?: string;
}

export interface NetSuiteVendor {
  internalId: string;
  name: string;
  
  email?: string | null;
  active?: boolean;

  address1?: string | null;
  address2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  country?: string | null;

  vendorExistInVlaw?: boolean;
  clientExistInVlaw?: boolean;
  firmExistInVlaw?: boolean;

  nativeEntityId?: number | null;
  nativeEntityType?: string | null;
  nativeName?: string | null;

  provider?: string;

  providerData?: Record<string, unknown>;
}

export interface NetSuiteVendorCreationData {
  name: string;
  entityId: string | number;
  legalName: string;
  email: string;
  taxId: string;
  phone: string;
  category: string;
  glCode: string;
  payableAccount: string;
  subsidiaryName: string;
  currency: string;
  isInactive: boolean;
  isPerson: boolean;
  is1099Eligible: boolean;
  address1?: string;
  address2?: string;
  city?: string;
  state?: string;
  zip?: string;
  country?: string;
  internalId: string;
}
